import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ToastType = 'success' | 'info' | 'warning' | 'error';
export type Toast = { id: number; type: ToastType; message: string; duration?: number };

type ToastContextType = {
  notify: (type: ToastType, message: string, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  const notify = useCallback((type: ToastType, message: string, duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, message, duration }]);
    if (duration && duration > 0) {
      setTimeout(() => remove(id), duration);
    }
  }, [remove]);

  const api = useMemo<ToastContextType>(() => ({
    notify,
    success: (m, d) => notify('success', m, d),
    info: (m, d) => notify('info', m, d),
    warning: (m, d) => notify('warning', m, d),
    error: (m, d) => notify('error', m, d),
  }), [notify]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-80 flex-col gap-2">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  const colorByType: Record<ToastType, string> = {
    success: 'bg-primary text-primary-foreground',
    info: 'bg-secondary text-secondary-foreground',
    warning: 'bg-accent text-accent-foreground',
    error: 'bg-destructive text-destructive-foreground',
  };
  return (
    <div className={`pointer-events-auto flex items-start gap-3 rounded px-3 py-2 text-sm shadow ${colorByType[toast.type]}`}>
      <div className="mt-0.5">
        {toast.type === 'success' ? '✓' : toast.type === 'info' ? 'ℹ︎' : toast.type === 'warning' ? '!' : '⚠︎'}
      </div>
      <div className="flex-1">{toast.message}</div>
      <button className="opacity-80 hover:opacity-100" onClick={onClose}>×</button>
    </div>
  );
};
